import { useCallback, useEffect, useRef, useState } from "react";
import { prepareDraftEnvironment, refreshEnvironmentFiles } from "@/lib/environment-files";
import { selectEffectiveEnvironmentId, useChatStore } from "@/stores/useChatStore";

let lastDraftEnvironmentId: string | null = null;
const preparedEnvironmentIds = new Set<string>();
const inFlightPreparations = new Map<string, Promise<void>>();

interface DraftEnvironmentPreparationOptions {
	enabled: boolean;
	chatId: string | null;
	newChatEnvironmentId: string | null;
	prepare?: (environmentId: string) => Promise<void>;
	refresh?: (environmentId: string) => Promise<boolean>;
	clear?: () => void;
}

export interface DraftEnvironmentPreparationFailure {
	environmentId: string;
	generation: number;
}

export interface DraftEnvironmentPreparationState {
	/** 現在選択中のドラフト環境の初期化または一覧取得が失敗しているか。 */
	preparationFailed: boolean;
	/** 現在選択中の環境の初期化を明示的に再試行する。 */
	retryPreparation: () => void;
}

export function isCurrentDraftPreparationFailure(
	failure: DraftEnvironmentPreparationFailure | null,
	generation: number,
	enabled: boolean,
	chatId: string | null,
	environmentId: string | null,
): boolean {
	return (
		enabled &&
		chatId === null &&
		environmentId !== null &&
		failure !== null &&
		failure.environmentId === environmentId &&
		failure.generation === generation
	);
}

/**
 * `/chats/new` の選択環境を初期化してファイル一覧を表示する。同じ環境への初期化は
 * 重複させず、環境を切り替えたときは旧ツリーを即座に破棄する。
 */
export async function prepareDraftEnvironmentIfNeeded({
	enabled,
	chatId,
	newChatEnvironmentId,
	prepare = prepareDraftEnvironment,
	refresh = refreshEnvironmentFiles,
	clear = () => useChatStore.getState().clearEnvironmentFiles(),
}: DraftEnvironmentPreparationOptions): Promise<void> {
	if (!enabled || chatId !== null || newChatEnvironmentId === null) return;

	const environmentId = newChatEnvironmentId;
	if (lastDraftEnvironmentId !== null && lastDraftEnvironmentId !== environmentId) {
		clear();
	}
	lastDraftEnvironmentId = environmentId;

	const existingPreparation = inFlightPreparations.get(environmentId);
	if (existingPreparation) {
		await existingPreparation;
		return;
	}

	if (preparedEnvironmentIds.has(environmentId)) {
		const refreshed = await refresh(environmentId);
		if (!refreshed) {
			const current = useChatStore.getState();
			if (current.chatId === null && selectEffectiveEnvironmentId(current) === environmentId) {
				clear();
			}
			throw new Error("ワークスペースファイルの取得に失敗しました。");
		}
		return;
	}

	const preparation = (async () => {
		await prepare(environmentId);
		preparedEnvironmentIds.add(environmentId);
	})();
	inFlightPreparations.set(environmentId, preparation);
	try {
		await preparation;
	} catch (err: unknown) {
		preparedEnvironmentIds.delete(environmentId);
		throw err;
	} finally {
		if (inFlightPreparations.get(environmentId) === preparation) {
			inFlightPreparations.delete(environmentId);
		}
	}
}

/** テスト間でドラフト環境の重複抑制状態をリセットする。 */
export function resetDraftEnvironmentPreparationStateForTests(): void {
	lastDraftEnvironmentId = null;
	preparedEnvironmentIds.clear();
	inFlightPreparations.clear();
}

/**
 * 新規チャットのドラフト画面でのみ、選択環境の初期化とファイル一覧取得を起動する。
 * リクエスト世代を保持し、環境・route・chat の切り替え後に古い失敗を表示しない。
 */
export function useDraftEnvironmentPreparation(enabled: boolean): DraftEnvironmentPreparationState {
	const chatId = useChatStore((s) => s.chatId);
	const newChatEnvironmentId = useChatStore((s) => s.newChatEnvironmentId);
	const [failure, setFailure] = useState<DraftEnvironmentPreparationFailure | null>(null);
	const requestGeneration = useRef(0);

	const recordFailure = useCallback((environmentId: string, generation: number, err: unknown) => {
		if (requestGeneration.current !== generation) return;

		const current = useChatStore.getState();
		if (current.chatId !== null || current.newChatEnvironmentId !== environmentId) return;

		console.error("useDraftEnvironmentPreparation failed", err);
		setFailure({ environmentId, generation });
	}, []);

	const runPreparation = useCallback(
		(options: DraftEnvironmentPreparationOptions, environmentId: string, generation: number) => {
			void prepareDraftEnvironmentIfNeeded(options).catch((err: unknown) => {
				recordFailure(environmentId, generation, err);
			});
		},
		[recordFailure],
	);

	useEffect(() => {
		const generation = ++requestGeneration.current;
		setFailure(null);

		if (!enabled || chatId !== null || newChatEnvironmentId === null) {
			return;
		}

		runPreparation({ enabled, chatId, newChatEnvironmentId }, newChatEnvironmentId, generation);
	}, [chatId, enabled, newChatEnvironmentId, runPreparation]);

	const retryPreparation = useCallback(() => {
		setFailure(null);
		const generation = ++requestGeneration.current;
		const current = useChatStore.getState();
		if (!enabled || current.chatId !== null || current.newChatEnvironmentId === null) return;

		runPreparation(
			{
				enabled: true,
				chatId: null,
				newChatEnvironmentId: current.newChatEnvironmentId,
			},
			current.newChatEnvironmentId,
			generation,
		);
	}, [enabled, runPreparation]);

	return {
		preparationFailed: isCurrentDraftPreparationFailure(
			failure,
			requestGeneration.current,
			enabled,
			chatId,
			newChatEnvironmentId,
		),
		retryPreparation,
	};
}
